import React, { useRef, useState } from 'react';
import { IonButton, IonCol, IonContent, IonGrid, IonHeader, IonIcon, IonLabel, IonPage, IonRow, IonSlide, IonSlides, IonText, IonModal } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { CountryRegion, RegionsByCountry } from '../utils/Regions';
import { ComputeRisk, isValid } from '../utils/CVRiskUtils';
import { ComputeBMI, GetCholesterolValues, GetHeightValues, GetWeightValues, GetBMI, FormatHeight } from '../utils/BMIUtils';
import { useUnitSystem, useCholesterolUnit, useCountry, Gender, CholesterolUnit } from '../utils/Settings';
import { Country } from '../utils/Regions';
import Toolbar from '../components/Toolbar';
import './CVRisk.scss';
import { arrowBackOutline } from 'ionicons/icons';
import HeightInput from '../components/HeightInput';
import NumericInput from '../components/NumericInput';
import { useGA4React } from 'ga-4-react';

const CVRisk: React.FC = () => {
  const { t } = useTranslation();
  const ga = useGA4React();
  const [gender, setGender] = useState(Gender.Male);
  const [isSmoker, setIsSmoker] = useState(false);
  const [isSmoker_initial, setIsSmokerInitial] = useState(false);
  const [isSmoker_if, setIsSmokerIf] = useState(false);
  const [isDiabetes, setIsDiabetes] = useState(false);
  const [systolicPressure, setSystolicPressure] = useState(120);
  const [systolicPressureInitial, setSystolicPressureInitial] = useState(120);
  const [systolicPressureIf, setSystolicPressureIf] = useState(120);
  const [cholesterol, setCholesterol] = useState(200);
  const [cholesterolInitial, setCholesterolInitial] = useState(200);
  const [cholesterolIf, setCholesterolIf] = useState(200);
  const [hasCholesterol, setHasCholesterol] = useState<boolean>();
  const [hasCardiovascularDisease, setHasCardiovascularDisease] = useState<boolean>();
  const [hasKidneyDisease, setHasKidneyDisease] = useState<boolean>();
  const [hasDiabetesMellitus, setHasDiabetesMellitus] = useState<boolean>();
  const [country] = useCountry();
  const isMexico = country === Country.México; // usa el mismo enum que ya tienes para México
  const [age, setAge] = useState<number>(40);
  const [height, setHeight] = useState<any>(175);
  const [weight, setWeight] = useState<any>(80);
  const [weightInitial, setWeightInitial] = useState<any>(80);
  const [weightIf, setWeightIf] = useState<any>(80);
  const [unitSystem] = useUnitSystem();
  const [cholesterolUnit] = useCholesterolUnit();
  const heightValues = GetHeightValues(unitSystem);
  const weightValues = GetWeightValues(unitSystem);
  const cholesterolValues = GetCholesterolValues(cholesterolUnit);
  const region: CountryRegion = RegionsByCountry.get(country) || CountryRegion.South;
  const [showModal, setShowModal] = useState(false);

  const bmi: number = ComputeBMI(weight, height, unitSystem);
  const riskLevel = [{
    level: 30,
    desc: 'Critical'
  }, {
    level: 20,
    desc: 'Very high'
  }, {
    level: 10,
    desc: 'High'
  }, {
    level: 5,
    desc: 'Moderate'
  }, {
    level: 0,
    desc: 'Low'
  }];
  const [risk] = ComputeRisk(
    region,
    gender,
    age,
    isSmoker,
    isDiabetes,
    systolicPressure,
    hasCholesterol!,
    cholesterol,
    cholesterolUnit,
    bmi);

  let sliderRef = useRef<HTMLIonSlidesElement>(null);

  if (ga)
    ga.event("calculateCVRisk", "result", risk.toString());

  function onSetCholesterol(value: boolean) {
    setHasCholesterol(value);
    setCholesterolInitial(200);
    setAge(40);
    setSystolicPressureInitial(120);
    setWeightInitial(80);
    setHeight(175);
    sliderRef.current?.slideNext();
  }

  function onSetCardiovascularDisease(value: boolean) {
    setHasCardiovascularDisease(value);
    if (value) {
      sliderRef.current?.slideNext();
      sliderRef.current?.slideNext();
      sliderRef.current?.slideNext();
      sliderRef.current?.slideNext();
      sliderRef.current?.slideNext();
      sliderRef.current?.slideNext();
    }
    sliderRef.current?.slideNext();
  }

  function onSetHasKidneyDisease(value: boolean) {
    setHasKidneyDisease(value);
    if (value) {
      sliderRef.current?.slideNext();
      sliderRef.current?.slideNext();
      sliderRef.current?.slideNext();
      sliderRef.current?.slideNext();
      sliderRef.current?.slideNext();
    }
    sliderRef.current?.slideNext();
  }

  function onSetHasDiabetesMellitus(value: boolean) {
    setHasDiabetesMellitus(value);
    if (value) {
      sliderRef.current?.slideNext();
      sliderRef.current?.slideNext();
      sliderRef.current?.slideNext();
      sliderRef.current?.slideNext();
    }
    sliderRef.current?.slideNext();
  }

  function goBackForm() {
    if (hasDiabetesMellitus) {
      setHasDiabetesMellitus(false);
      sliderRef.current?.slidePrev();
      sliderRef.current?.slidePrev();
      sliderRef.current?.slidePrev();
    } else if (hasKidneyDisease) {
      setHasKidneyDisease(false);
      sliderRef.current?.slidePrev();
      sliderRef.current?.slidePrev();
      sliderRef.current?.slidePrev();
      sliderRef.current?.slidePrev();
    } else if (hasCardiovascularDisease) {
      setHasCardiovascularDisease(false);
      sliderRef.current?.slidePrev();
      sliderRef.current?.slidePrev();
      sliderRef.current?.slidePrev();
      sliderRef.current?.slidePrev();
      sliderRef.current?.slidePrev();
    }
    sliderRef.current?.slidePrev();
  }

  function newCVDrisk() {
    sliderRef.current?.slideTo(0);
  }


  function onCalculate() {
    setCholesterol(cholesterolInitial);
    setCholesterolIf(cholesterolInitial);
    setIsSmoker(isSmoker_initial);
    setIsSmokerIf(isSmoker_initial);
    setSystolicPressure(systolicPressureInitial);
    setSystolicPressureIf(systolicPressureInitial);
    setWeight(weightInitial);
    setWeightIf(weightInitial);
    setIsDiabetes(false);
    if ((cholesterolInitial > 300 && cholesterolUnit === CholesterolUnit.mgdl) ||
      (cholesterolInitial >= 7.5 && cholesterolUnit === CholesterolUnit.mmol)) {
      setShowModal(true);
    }
    sliderRef.current?.slideNext();
  }

  function onReCalulate() {
    setCholesterol(cholesterolIf);
    setIsSmoker(isSmoker_if);
    setSystolicPressure(systolicPressureIf);
    setWeight(weightIf);
    setIsDiabetes(false);
    if ((cholesterolIf >= 301 && cholesterolUnit === CholesterolUnit.mgdl) ||
      (cholesterolIf >= 7.5 && cholesterolUnit === CholesterolUnit.mmol)) {
      setShowModal(true);
    }
  }

  let isValidAge = isValid(age, 40, 75);
  let isValidSystolicPressureInitial = isValid(systolicPressureInitial, 90, 200);
  let isValidCholesterolInitial = isValid(cholesterolInitial, cholesterolValues[0], cholesterolValues[1]);
  let isValidSystolicPressureIf = isValid(systolicPressureIf, 90, 200);
  let isValidCholesterolIf = isValid(cholesterolIf, cholesterolValues[0], cholesterolValues[1]);
  let isValidWeight = isValid(weight, weightValues[0], weightValues[1]);
  let isValidWeightIf = isValid(weightIf, weightValues[0], weightValues[1]);
  let isValidHeight = isValid(height, heightValues[0], heightValues[1]);
  let enableCalculate = isValidAge && isValidSystolicPressureInitial && (hasCholesterol ? isValidCholesterolInitial : isValidWeight && isValidHeight);
  let enableRecalculate = isValidSystolicPressureIf && (hasCholesterol ? isValidCholesterolIf : isValidWeightIf);

  let sliderOptions = {
    followFinger: false,
    allowTouchMove: false
  };

  return (
    <IonPage id="cvrisk-page">
      <IonHeader>
        <Toolbar title={t('Cardiovascular Risk')} />
      </IonHeader>
      <IonContent fullscreen >
        <IonSlides className="calculatorSlides" ref={sliderRef} pager={true} scrollbar={true} options={sliderOptions}>
          <IonSlide>
            <IonGrid className="dashboard calculatorGrid">
              <IonRow>
                <IonText color="primary"><h1 className="ion-hide-xl-up"><b>{t('Cardiovascular Risk')}</b></h1></IonText>
              </IonRow>
              <IonRow className="cholesterolPanel">
                <IonCol size="12" class="ion-text-center">
                  <h2><b>{t('CardiovascularHistoryHave')}</b></h2>
                  <h5>{t('CardiovascularCategory')}</h5>
                </IonCol>
                <IonCol size="12" class="ion-text-center">
                  <IonButton size="large" color={hasCardiovascularDisease === true ? 'primary' : 'dark'} fill={hasCardiovascularDisease === true ? 'solid' : 'outline'}
                    onClick={() => onSetCardiovascularDisease(true)}>{t('Yes')}</IonButton>
                  <IonButton size="large" color={hasCardiovascularDisease === true ? 'dark' : 'primary'} fill={hasCardiovascularDisease === true ? 'outline' : 'solid'}
                    onClick={() => onSetCardiovascularDisease(false)}>{t('No')}</IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonSlide>
          <IonSlide>
            <IonGrid className="dashboard calculatorGrid">
              <IonRow>
                <IonText color="primary"><h1 className="ion-hide-xl-up"><b>{t('Cardiovascular Risk')}</b></h1></IonText>
              </IonRow>
              <IonRow>
                <IonCol size="4">
                  <IonButton className="backButton" fill="clear" onClick={goBackForm}>
                    <IonIcon icon={arrowBackOutline} slot="icon-only" />
                  </IonButton>
                </IonCol>
              </IonRow>
              <IonRow className="cholesterolPanel">
                <IonCol size="12" class="ion-text-center">
                  <h2><b>{t('ChronicDiseaseHave')}</b></h2>
                </IonCol>
                <IonCol size="12" class="ion-text-center">
                  <IonButton size="large" color={hasKidneyDisease === true ? 'primary' : 'dark'} fill={hasKidneyDisease === true ? 'solid' : 'outline'}
                    onClick={() => onSetHasKidneyDisease(true)}>{t('Yes')}</IonButton>
                  <IonButton size="large" color={hasKidneyDisease === true ? 'dark' : 'primary'} fill={hasKidneyDisease === true ? 'outline' : 'solid'}
                    onClick={() => onSetHasKidneyDisease(false)}>{t('No')}</IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonSlide>
          
          <IonSlide>
  <IonGrid className="dashboard calculatorGrid">
    <IonRow>
      <IonText color="primary">
        <h1 className="ion-hide-xl-up"><b>{t('Cardiovascular Risk')}</b></h1>
      </IonText>
    </IonRow>

    <IonRow>
      <IonCol size="4">
        <IonButton className="backButton" fill="clear" onClick={goBackForm}>
          <IonIcon icon={arrowBackOutline} slot="icon-only" />
        </IonButton>
      </IonCol>
    </IonRow>

    <IonRow className="cholesterolPanel">
      <IonCol size="12" class="ion-text-center">
        <h2><b>{t('DiabetesHave')}</b></h2>
      </IonCol>

      <IonCol size="12" class="ion-text-center">
        <IonButton
          size="large"
          color={hasDiabetesMellitus === true ? 'primary' : 'dark'}
          fill={hasDiabetesMellitus === true ? 'solid' : 'outline'}
          onClick={() => onSetHasDiabetesMellitus(true)}
        >
          {t('Yes')}
        </IonButton>

        <IonButton
          size="large"
          color={hasDiabetesMellitus === true ? 'dark' : 'primary'}
          fill={hasDiabetesMellitus === true ? 'outline' : 'solid'}
          onClick={() => onSetHasDiabetesMellitus(false)}
        >
          {t('No')}
        </IonButton>
      </IonCol>
    </IonRow>
  </IonGrid>
</IonSlide>

{/* 
<IonRow hidden={!hasCholesterol}>
  <IonCol size="12">
    <IonLabel><b>{t('Diabetes')}</b></IonLabel>
  </IonCol>
  <IonCol size="3">
    <IonButton color={isDiabetes === true ? 'primary' : 'dark'} fill={isDiabetes === true ? 'solid' : 'outline'} onClick={() => setIsDiabetes(true)}>{t('Yes')}</IonButton>
  </IonCol>
  <IonCol size="3">
    <IonButton color={isDiabetes === false ? 'primary' : 'dark'} fill={isDiabetes === false ? 'solid' : 'outline'} onClick={() => setIsDiabetes(false)}>{t('No')}</IonButton>
  </IonCol>
</IonRow>
*/}


          <IonSlide>
            <IonGrid className="dashboard calculatorGrid">
              <IonRow>
                <IonText color="primary"><h1 className="ion-hide-xl-up"><b>{t('Cardiovascular Risk')}</b></h1></IonText>
              </IonRow>
              <IonRow>
                <IonCol size="4">
                  <IonButton className="backButton" fill="clear" onClick={goBackForm}>
                    <IonIcon icon={arrowBackOutline} slot="icon-only" />
                  </IonButton>
                </IonCol>
              </IonRow>
              <IonRow className="cholesterolPanel">
                <IonCol size="12" class="ion-text-center">
                  <h2><b>{t('CholesterolKnown')}</b></h2>
                </IonCol>
                <IonCol size="12" class="ion-text-center">
                  <IonButton size="large" color={hasCholesterol === true ? 'primary' : 'dark'} fill={hasCholesterol === true ? 'solid' : 'outline'} onClick={() => onSetCholesterol(true)}>{t('Yes')}</IonButton>
                  <IonButton size="large" color={hasCholesterol === true ? 'dark' : 'primary'} fill={hasCholesterol === true ? 'outline' : 'solid'} onClick={() => onSetCholesterol(false)}>{t('No')}</IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonSlide>
          <IonSlide>
            <IonGrid className="dashboard calculatorGrid">
              <IonRow>
                <IonText color="primary"><h1 className="ion-hide-xl-up"><b>{t('Cardiovascular Risk')}</b></h1></IonText>
              </IonRow>
              <IonRow>
                <IonCol size="4">
                  <IonButton className="backButton" fill="clear" onClick={goBackForm}>
                    <IonIcon icon={arrowBackOutline} slot="icon-only" />
                  </IonButton>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size-sm="6" size="12">
                  <IonRow>
                    <IonCol size="12">
                      <IonLabel><b>{t('Gender')}</b></IonLabel>
                    </IonCol>
                    <IonCol>
                      <IonButton color={gender === Gender.Female ? 'primary' : 'dark'} fill={gender === Gender.Female ? 'solid' : 'outline'} onClick={() => setGender(Gender.Female)}>{t('Female')}</IonButton>
                    </IonCol>
                    <IonCol>
                      <IonButton color={gender === Gender.Male ? 'primary' : 'dark'} fill={gender === Gender.Male ? 'solid' : 'outline'} onClick={() => setGender(Gender.Male)}>{t('Male')}</IonButton>
                    </IonCol>
                    <IonCol size="12">
                      <IonLabel><b>{t('Smoker')}</b><br hidden={hasCholesterol} />&nbsp;</IonLabel>
                    </IonCol>
                    <IonCol size="3">
                      <IonButton color={isSmoker_initial === true ? 'primary' : 'dark'} fill={isSmoker_initial === true ? 'solid' : 'outline'} onClick={() => setIsSmokerInitial(true)}>{t('Yes')}</IonButton>
                    </IonCol>
                    <IonCol size="3">
                      <IonButton color={isSmoker_initial === false ? 'primary' : 'dark'} fill={isSmoker_initial === false ? 'solid' : 'outline'} onClick={() => setIsSmokerInitial(false)}>{t('No')}</IonButton>
                    </IonCol>
                  </IonRow>
                  <IonRow hidden={!hasCholesterol}>
                    <IonCol size="12">
                      <IonLabel><b>{t('Cholesterol')} {t('Total')}</b><br />({cholesterolValues[2]}):</IonLabel>
                    </IonCol>
                    <IonCol size="12">
                      <NumericInput value={cholesterolInitial} min={cholesterolValues[0]} max={cholesterolValues[1]} onValueChange={(value: number) => setCholesterolInitial(value)} />
                    </IonCol>
                  </IonRow>
                  <IonRow hidden={hasCholesterol}>
                    <IonCol size="12">
                      <IonLabel><b>{t('Weight')}</b><br />({weightValues[2]})</IonLabel>
                    </IonCol>
                    <IonCol size="12">
                      <NumericInput value={weightInitial} min={weightValues[0]} max={weightValues[1]} onValueChange={(value: number) => setWeightInitial(value)} />
                    </IonCol>
                  </IonRow>
                </IonCol>
                <IonCol size-sm="5" offset-sm="1" size="12">
                  <IonRow>
                    <IonCol size="12">
                      <IonLabel><b>{t('Age')}</b></IonLabel>
                    </IonCol>
                    <IonCol size="12">
                      <NumericInput value={age} min={40} max={75} onValueChange={(value: number) => setAge(value)} />
                    </IonCol>
                  </IonRow>
                 
                  <IonRow>
                    <IonCol size="12">
                      <IonLabel><b>{t('SystolicPressure')}</b><br />(mmHg)</IonLabel>
                    </IonCol>
                    <IonCol size="12">
                      <NumericInput value={systolicPressureInitial} min={90} max={200} onValueChange={(value: number) => setSystolicPressureInitial(value)} />
                    </IonCol>
                  </IonRow>
                  <IonRow hidden={hasCholesterol}>
                    <IonCol size="12">
                      <IonLabel><b>{t('Height')}</b><br />({heightValues[2]}):</IonLabel>
                    </IonCol>
                    <IonCol size="12">
                      <HeightInput height={height} setHeight={(height: number) => setHeight(height)} />
                    </IonCol>

                  </IonRow>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12" size-sm="4">
                  <IonButton className="calculateButton" disabled={!enableCalculate} fill="solid" onClick={onCalculate}>{t('Calculate')}</IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonSlide>
          <IonSlide>
            <IonGrid className="dashboard calculatorGrid">
              <IonRow>
                <IonRow>
                  <IonText color="primary"><h1 className="ion-hide-xl-up"><b>{t('Cardiovascular Risk')}</b></h1></IonText>
                </IonRow>
                <IonCol size="12">
                  <IonButton className="backButton" fill="clear" onClick={goBackForm}>
                    <IonIcon icon={arrowBackOutline} slot="icon-only" />
                  </IonButton>
                  {
                    isMexico && (
                     <div className="mx-high-risk-box">
                        <div
                          className="mx-risk-text"
                          dangerouslySetInnerHTML={{
                            __html:
                              Number(risk) >= 10
                                ? t('mxHighRiskFinalText')
                                : t('mxLowRiskFinalText')
                          }}
                        ></div>
                      </div>

                    )
                  }

{
  !isMexico && (
    <IonButton
      className="GoToClinicalPathwayButton"
      routerLink="/recommendations"
      fill="solid"
    >
      {t('GoClinicalPathway')}
    </IonButton>
  )
}


                </IonCol>
                <IonCol size="12" size-sm="6">
                  <IonLabel className="riskValue">{t('10 Years CV Risk')}: {risk}%</IonLabel>
                  <IonGrid className="riskGrid">
                    {
                      riskLevel.map((risklevel: any, index: number) => {
                        return <IonRow key={index}>
                          <IonCol size="4" className={`risk${4 - index}`}>&nbsp;
                            {
                              risk >= risklevel.level &&
                              (index === 0 || Number(risk) < riskLevel[index - 1]!.level) && (
                                <IonLabel>&lt;&nbsp;{t(risklevel.desc)}</IonLabel>
                              )
                            }
                          </IonCol>
                          <IonCol size="8" className={`riskDesc${4 - index}`}>&nbsp;
                            {
                              risk >= risklevel.level &&
                              (index === 0 || Number(risk) < riskLevel[index - 1]!.level) && (
                                <IonLabel>&lt;&nbsp;{t(risklevel.desc)}</IonLabel>
                              )
                            }
                          </IonCol>
                        </IonRow>
                      })
                    }
                  </IonGrid>
                </IonCol>
                <IonCol size="12" size-sm="5" offset-sm="1">
                  <IonLabel className="inputDataLabel">{t('InputData')}</IonLabel>
                  <table className="inputDataTable">
                    <tbody>
                      <tr>
                        <td>{t('Country')}</td>
                        <td>{t(country)}</td>
                      </tr>
                      <tr>
                        <td>{t('Gender')}</td>
                        <td>{t(gender)}</td>
                      </tr>
                      <tr>
                        <td>{t('Age')}</td>
                        <td>{age}</td>
                      </tr>
                      <tr>
                        <td>{t('Smoker')}</td>
                        <td>{t(isSmoker ? 'Yes' : 'No')}</td>
                      </tr>
                      <tr>
                        <td>{t('SystolicPressure')}</td>
                        <td className={`${systolicPressure >= 140 ? 'alert' : ''}`}>{systolicPressure} mmHg</td>
                      </tr>
                      <tr>
                        <td>{t('Cholesterol')}</td>
                        <td>{hasCholesterol ? `${cholesterol} ${cholesterolValues[2]}` : t('No')}</td>
                      </tr>
                      {
                        hasCholesterol &&
                        <tr>
                          <td>{t('Diabetes')}</td>
                          <td>{t(isDiabetes ? 'Yes' : 'No')}</td>
                        </tr>
                      }
                      {
                        !hasCholesterol &&
                        <tr>
                          <td>{t('Weight')}</td>
                          <td>{weight} {weightValues[2]}</td>
                        </tr>
                      }
                      {
                        !hasCholesterol &&
                        <tr>
                          <td>{t('Height')}</td>
                          <td>{FormatHeight(height, unitSystem)}</td>
                        </tr>
                      }
                      {
                        !hasCholesterol &&
                        <tr>
                          <td>{t('BMI')}</td>
                          <td>{GetBMI(weight, height, unitSystem)}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </IonCol>
                <IonCol size="12" size-sm="12">
                  <IonLabel><b>{t('What Would Happen If')}</b></IonLabel>
                </IonCol>
                <IonCol size="12" size-sm="4">
                  <IonRow>
                    <IonCol size="12" style={{ 'lineHeight': '40px' }}>
                      <IonLabel><b>{t('Smoker')}</b></IonLabel>
                    </IonCol>
                    <IonCol size="4" size-sm="5">
                      <IonButton color={isSmoker_if === true ? 'primary' : 'dark'} fill={isSmoker_if === true ? 'solid' : 'outline'} onClick={() => setIsSmokerIf(true)}>{t('Yes')}</IonButton>
                    </IonCol>
                    <IonCol size="4" size-sm="5">
                      <IonButton color={isSmoker_if === false ? 'primary' : 'dark'} fill={isSmoker_if === false ? 'solid' : 'outline'} onClick={() => setIsSmokerIf(false)}>{t('No')}</IonButton>
                    </IonCol>
                  </IonRow>
                </IonCol>
                <IonCol size="12" size-sm="4" hidden={!hasCholesterol}>
                  <IonRow>
                    <IonCol size="12">
                      <IonLabel><b>{t('Cholesterol')} {t('Total')}</b><br />({cholesterolValues[2]}):</IonLabel>
                    </IonCol>
                    <IonCol size="12">
                      <NumericInput value={cholesterolIf} min={cholesterolValues[0]} max={cholesterolValues[1]} onValueChange={(value: number) => setCholesterolIf(value)} />
                    </IonCol>
                  </IonRow>
                </IonCol>
                <IonCol size="12" size-sm="4">
                  <IonRow>
                    <IonCol size="12">
                      <IonLabel><b>{t('SystolicPressure')}</b><br />(mmHg)</IonLabel>
                    </IonCol>
                    <IonCol size="12">
                      <NumericInput value={systolicPressureIf} min={90} max={200} onValueChange={(value: number) => setSystolicPressureIf(value)} />
                    </IonCol>
                  </IonRow>
                </IonCol>
                <IonCol size="12" size-sm="4" hidden={hasCholesterol}>
                  <IonRow>
                    <IonCol size="12">
                      <IonLabel><b>{t('Weight')}</b><br />({weightValues[2]})</IonLabel>
                    </IonCol>
                    <IonCol size="12">
                      <NumericInput value={weightIf} min={weightValues[0]} max={weightValues[1]} onValueChange={(value: number) => setWeightIf(value)} />
                    </IonCol>
                  </IonRow>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12" size-sm="4">
                  <IonButton className="calculateButton" disabled={!enableRecalculate} fill="solid" onClick={onReCalulate}>{t('Recalculate')}</IonButton>
                </IonCol>
                <IonCol size="12" size-sm="8">
                  <IonButton className="calculateButton" fill="solid" onClick={newCVDrisk}>{t('NewCVDCalculate')}</IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonSlide>
          <IonSlide>
            <IonGrid className="dashboard calculatorGrid">
              <IonRow>
                <IonText color="primary"><h1 className="ion-hide-xl-up"><b>{t('Cardiovascular Risk')}</b></h1></IonText>
              </IonRow>
              <IonRow>
                <IonCol size="4">
                  <IonButton className="backButton" fill="clear" onClick={goBackForm}>
                    <IonIcon icon={arrowBackOutline} slot="icon-only" />
                  </IonButton>
                </IonCol>
              </IonRow>
             <IonRow>
  {isMexico ? (
    <>
      {/* 🟠 MÉXICO – DIABETES MELLITUS → Paciente de Alto Riesgo */}
      {hasDiabetesMellitus && (
        <IonCol size="12">
          <div className="mx-high-risk-box">
            <div className="mx-risk-title">
              {t('highCVDRisk') /* “Paciente de Alto Riesgo” */}
            </div>

            <div
              className="mx-risk-text"
              dangerouslySetInnerHTML={{
                __html: t('mxDiabetesRecommendations')
              }}
            />
          </div>
        </IonCol>
      )}

      {/* 🟠 MÉXICO – ENFERMEDAD RENAL → Paciente de Alto Riesgo */}
      {!hasDiabetesMellitus && hasKidneyDisease && (
        <IonCol size="12">
          <div className="mx-high-risk-box">
            <div className="mx-risk-title">
              {t('highCVDRisk') /* “Paciente de Alto Riesgo” */}
            </div>

            <div
              className="mx-risk-text"
              dangerouslySetInnerHTML={{
                __html: t('mxKidneyRecommendations')
              }}
            />
          </div>
        </IonCol>
      )}

      {/* 🔴 MÉXICO – ENFERMEDAD CV ESTABLECIDA → Paciente de muy Alto Riesgo */}
      {!hasDiabetesMellitus && !hasKidneyDisease && hasCardiovascularDisease && (
        <IonCol size="12">
          <div className="mx-high-risk-box">
            <div className="mx-risk-title">
              {t('veryHighCVDRisk') /* “Paciente de muy Alto Riesgo” */}
            </div>

            <div
              className="mx-risk-text"
              dangerouslySetInnerHTML={{
                __html: t('mxVeryHighRiskRecommendations')
              }}
            />
          </div>
        </IonCol>
      )}
    </>
  ) : (
    /* 🌍 RESTO DE PAÍSES – deja tu diseño anterior */
    <>
      {hasDiabetesMellitus ? (
        <IonCol size="12">
          <div className="high_risk">{t('highCVDRisk')}</div>
          <h4
            dangerouslySetInnerHTML={{
              __html: t('therapeuticRecommendations')
            }}
          ></h4>
          <IonButton
            className="GoToClinicalPathwayButton"
            routerLink="/recommendations"
            fill="solid"
          >
            {t('GoClinicalPathway')}
          </IonButton>
        </IonCol>
      ) : hasKidneyDisease ? (
        <IonCol size="12">
          <div className="high_risk">{t('highCVDRisk')}</div>
          <h4
            dangerouslySetInnerHTML={{
              __html: t('therapeuticRecommendations')
            }}
          ></h4>
          <IonButton
            className="GoToClinicalPathwayButton"
            routerLink="/recommendations"
            fill="solid"
          >
            {t('GoClinicalPathway')}
          </IonButton>
        </IonCol>
      ) : hasCardiovascularDisease ? (
        <IonCol size="12">
          <div className="very_high_risk">{t('veryHighCVDRisk')}</div>
          <h4
            dangerouslySetInnerHTML={{
              __html: t('therapeuticRecommendations')
            }}
          ></h4>
          <IonButton
            className="GoToClinicalPathwayButton"
            routerLink="/recommendations"
            fill="solid"
          >
            {t('GoClinicalPathway')}
          </IonButton>
        </IonCol>
      ) : null}
    </>
  )}
</IonRow>


            </IonGrid>
          </IonSlide>
        </IonSlides>

        <IonModal isOpen={showModal} cssClass='my-custom-modal-css'>
          <p className="ion-text-center">{t('highCVDRisk4')}</p>
          <IonButton onClick={() => setShowModal(false)}>Close Modal</IonButton>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default CVRisk;
